import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, CheckCircle2, Send, PackageCheck } from "lucide-react";
import client, { extractError } from "@/api";
import { CommercialInvoicePanel } from "@/components/CommercialInvoicePanel";

const STATUS_LABEL = {
  PENDING_FULFILMENT: "Awaiting fulfilment",
  FULFILMENT_CONFIRMED: "Awaiting settlement",
  SETTLEMENT_PENDING: "Settlement pending",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
  DISPUTED: "Disputed",
};

const FULFILMENT_LABEL = {
  NOT_STARTED: "Not started",
  SELLER_DELIVERED: "Seller delivered",
  BUYER_CONFIRMED: "Buyer confirmed",
};

const SETTLEMENT_LABEL = {
  NOT_STARTED: "Not started",
  RESERVED: "Reserved",
  SETTLED: "Settled",
  FAILED: "Failed",
  REVERSED: "Released",
};

const FILTERS = [
  { value: "", label: "All" },
  { value: "PENDING_FULFILMENT", label: "Fulfilment" },
  { value: "FULFILMENT_CONFIRMED", label: "Awaiting settle" },
  { value: "SETTLEMENT_PENDING", label: "Settlement pending" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
];

const DIRECTIONS = [
  { value: "any", label: "All" },
  { value: "buyer", label: "As buyer" },
  { value: "seller", label: "As seller" },
];

function Timeline({ txn }) {
  const steps = [
    { key: "TRANSACTION_CREATED", label: "Transaction created", at: txn.created_at, done: true },
    { key: "FULFILMENT_STARTED", label: "Seller delivered", at: txn.fulfilment_started_at,
       done: !!txn.fulfilment_started_at },
    { key: "FULFILMENT_CONFIRMED", label: "Buyer confirmed", at: txn.fulfilment_confirmed_at,
       done: !!txn.fulfilment_confirmed_at },
    { key: "CREDIT_RESERVED", label: "Credit reserved", at: txn.settlement_authorised_at,
       done: !!txn.settlement_authorised_at },
    { key: "SETTLEMENT_COMPLETED", label: "Settlement complete", at: txn.settled_at,
       done: !!txn.settled_at },
  ];
  return (
    <ol className="txn-timeline" data-testid="txn-timeline">
      {steps.map((s) => (
        <li key={s.key} className={s.done ? "done" : "pending"}>
          <div className="dot" />
          <div>
            <strong>{s.label}</strong>
            <small className="muted">{s.at ? new Date(s.at).toLocaleString() : "—"}</small>
          </div>
        </li>
      ))}
    </ol>
  );
}

function Detail({ id, onBack }) {
  const [txn, setTxn] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const load = async () => {
    try { const r = await client.get(`/transactions/${id}`); setTxn(r.data); }
    catch (err) { setError(extractError(err, "Unable to load transaction.")); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  if (!txn) return <div className="loading-block">Loading…</div>;

  const act = async (path, label) => {
    setBusy(true); setError(""); setMessage("");
    try { const r = await client.post(`/transactions/${id}/${path}`, {}); setTxn(r.data); setMessage(label); }
    catch (err) { setError(extractError(err)); }
    finally { setBusy(false); }
  };

  const canStart = txn.viewer_side === "SELLER" && txn.fulfilment_status === "NOT_STARTED" && txn.status === "PENDING_FULFILMENT";
  const canConfirm = txn.viewer_side === "BUYER" && txn.fulfilment_status === "SELLER_DELIVERED" && txn.status === "PENDING_FULFILMENT";

  return (
    <div className="admin-company-detail" data-testid="transaction-detail">
      <button type="button" className="link-button back" data-testid="transaction-back" onClick={onBack}><ChevronLeft size={14} /> Back to transactions</button>
      <header className="detail-header">
        <div>
          <p className="eyebrow">{STATUS_LABEL[txn.status]}</p>
          <h2>{txn.listing_title}</h2>
          <p className="muted">Buyer: {txn.buyer_name} · Seller: {txn.seller_name} · Your role: <strong>{txn.viewer_side}</strong></p>
        </div>
        <div className="chip-stack">
          <span className={`status-chip status-${txn.status.toLowerCase()}`}>{STATUS_LABEL[txn.status]}</span>
          <span className={`status-chip status-${txn.settlement_status.toLowerCase()}`}>Settlement: {SETTLEMENT_LABEL[txn.settlement_status]}</span>
        </div>
      </header>
      {error && <div className="form-error">{error}</div>}
      {message && <div className="form-success">{message}</div>}
      <section className="detail-grid">
        <article><p className="eyebrow">TRADE VALUE</p><p><strong>₹{txn.trade_value.toLocaleString("en-IN")}</strong></p></article>
        <article><p className="eyebrow">FACILITATOR FEE ({txn.facilitator_fee_rate_percent}%)</p><p>₹{txn.facilitator_fee_amount.toLocaleString("en-IN")}</p></article>
        <article><p className="eyebrow">QUANTITY</p><p>{txn.quantity ?? "—"}</p></article>
        <article><p className="eyebrow">UNIT VALUE</p><p>₹{txn.unit_value.toLocaleString("en-IN")}</p></article>
        <article><p className="eyebrow">SETTLEMENT REF</p><p>{txn.settlement_reference || "—"}</p></article>
        <article><p className="eyebrow">CREATED</p><p>{new Date(txn.created_at).toLocaleString()}</p></article>
      </section>
      <section className="section-block">
        <p className="eyebrow">FINANCIAL SUMMARY</p>
        <p className="muted small">Barter India acts as the facilitator only. Buyer and seller invoice each other directly for the underlying goods or services. The 10% facilitator fee is retained by Barter India as a separate line item and is <strong>not</strong> deducted from the seller's trade credit.</p>
        <ul className="finance-list">
          <li><span>Buyer credit impact</span><strong data-testid="buyer-impact">−₹{txn.trade_value.toLocaleString("en-IN")}</strong></li>
          <li><span>Seller credit impact</span><strong data-testid="seller-impact">+₹{txn.trade_value.toLocaleString("en-IN")}</strong></li>
          <li><span>Facilitator fee (retained by Barter India)</span><strong>₹{txn.facilitator_fee_amount.toLocaleString("en-IN")}</strong></li>
        </ul>
      </section>
      <section className="section-block">
        <p className="eyebrow">TIMELINE</p>
        <Timeline txn={txn} />
      </section>
      <CommercialInvoicePanel transaction={txn} />
      {(canStart || canConfirm) && (
        <section className="section-block" data-testid="transaction-actions">
          <div className="button-row">
            {canStart && <button type="button" className="primary-button" onClick={() => act("fulfilment/start", "Fulfilment started.")} disabled={busy} data-testid="start-fulfilment"><PackageCheck size={14} /> Mark as delivered</button>}
            {canConfirm && <button type="button" className="primary-button" onClick={() => act("fulfilment/confirm", "Fulfilment confirmed. Awaiting admin settlement.")} disabled={busy} data-testid="confirm-fulfilment"><CheckCircle2 size={14} /> Confirm receipt</button>}
          </div>
        </section>
      )}
      {txn.audit_log?.length > 0 && (
        <section className="documents-section">
          <div className="section-heading"><div><p className="eyebrow">HISTORY</p><h3>Audit trail</h3></div></div>
          <ul className="audit-list" data-testid="transaction-audit">
            {txn.audit_log.map((a) => (
              <li key={a.id}>
                <strong>{a.event}</strong><small>{a.previous_state || "—"} → {a.new_state || "—"}</small>
                <small className="muted">{a.actor_email || "system"} · {new Date(a.created_at).toLocaleString()}</small>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export default function TransactionsPage() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [direction, setDirection] = useState("any");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      params.set("direction", direction);
      const r = await client.get(`/transactions?${params.toString()}`);
      setItems(r.data || []);
    } catch (err) { setError(extractError(err, "Unable to load transactions.")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, direction]);

  const counts = useMemo(() => {
    const c = {}; items.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; }); return c;
  }, [items]);

  if (detailId) return <Detail id={detailId} onBack={() => { setDetailId(null); load(); }} />;

  return (
    <div className="my-listings-page" data-testid="transactions-page">
      <div className="admin-toolbar">
        <div className="filter-tabs" data-testid="txn-status-tabs">
          {FILTERS.map((f) => (
            <button key={f.value || "all"} type="button" data-testid={`txn-filter-${f.value || "all"}`} className={status === f.value ? "active" : ""} onClick={() => setStatus(f.value)}>
              {f.label}{f.value && counts[f.value] > 0 && <span className="count">{counts[f.value]}</span>}
            </button>
          ))}
        </div>
        <div className="filter-tabs" data-testid="txn-direction-tabs">
          {DIRECTIONS.map((d) => (
            <button key={d.value} type="button" data-testid={`txn-direction-${d.value}`} className={direction === d.value ? "active" : ""} onClick={() => setDirection(d.value)}>{d.label}</button>
          ))}
        </div>
      </div>
      {error && <div className="form-error">{error}</div>}
      {loading ? <div className="loading-block">Loading…</div> : (
        <table className="admin-table" data-testid="transactions-table">
          <thead><tr><th>Listing</th><th>Role</th><th>Counterparty</th><th>Trade value</th><th>Fee</th><th>Fulfilment</th><th>Settlement</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={9} className="empty-row">No transactions yet. Approved trade requests move here.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} data-testid={`transaction-row-${r.id}`}>
                <td><strong>{r.listing_title}</strong><br /><small className="muted">{r.id.slice(0, 8)}</small></td>
                <td><small>{r.viewer_side}</small></td>
                <td>{r.viewer_side === "BUYER" ? r.seller_name : r.buyer_name}</td>
                <td>₹{r.trade_value.toLocaleString("en-IN")}</td>
                <td>₹{r.facilitator_fee_amount.toLocaleString("en-IN")}</td>
                <td><small>{FULFILMENT_LABEL[r.fulfilment_status]}</small></td>
                <td><small>{SETTLEMENT_LABEL[r.settlement_status]}</small></td>
                <td><span className={`status-chip status-${r.status.toLowerCase()}`}>{STATUS_LABEL[r.status]}</span></td>
                <td><button type="button" className="link-button" data-testid={`open-transaction-${r.id}`} onClick={() => setDetailId(r.id)}>Open →</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
